import { Controller, useFormContext } from 'react-hook-form';
import { Input, PhoneInput } from '@ds';
import { AdminFormRow, useSchema } from '@ui';
import { ContactFormType } from '@/types';
import { isFieldRequired } from '@/utils/helpers';
import { useCountriesData } from '@/contexts/CountriesDataProvider';

type MainContactFormProps = {
  isReadOnly?: boolean;
  showEmail?: boolean;
  requiredFields?: Partial<Record<keyof ContactFormType, boolean>>;
};

export const MainContactForm = ({ isReadOnly = false, showEmail = true, requiredFields }: MainContactFormProps) => {
  const { control } = useFormContext<ContactFormType>();
  const schema = useSchema();
  const { countryCodes } = useCountriesData();

  const getIsRequired = (fieldName: keyof ContactFormType) => {
    if (requiredFields?.[fieldName] !== undefined) {
      return requiredFields[fieldName];
    }

    return isFieldRequired(schema, fieldName);
  };

  return (
    <>
      <AdminFormRow title="First Name" isRequired={getIsRequired('firstName')}>
        <Controller
          name="firstName"
          control={control}
          render={({ field, fieldState: { invalid, error } }) => (
            <Input
              aria-label="Contact First Name"
              type="text"
              isReadOnly={isReadOnly}
              isDisabled={isReadOnly}
              isInvalid={invalid}
              errorMessage={error?.message}
              {...field}
            />
          )}
        />
      </AdminFormRow>

      <AdminFormRow title="Last Name" isRequired={getIsRequired('lastName')}>
        <Controller
          name="lastName"
          control={control}
          render={({ field, fieldState: { invalid, error } }) => (
            <Input
              aria-label="Contact Last Name"
              type="text"
              isInvalid={invalid}
              isReadOnly={isReadOnly}
              isDisabled={isReadOnly}
              errorMessage={error?.message}
              {...field}
            />
          )}
        />
      </AdminFormRow>

      {showEmail && (
        <AdminFormRow title="Email" isRequired={getIsRequired('email')}>
          <Controller
            name="email"
            control={control}
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <Input
                aria-label="Contact Email"
                type="email"
                isInvalid={invalid}
                isReadOnly={isReadOnly}
                isDisabled={isReadOnly}
                errorMessage={error?.message}
                value={value || ''}
                {...field}
              />
            )}
          />
        </AdminFormRow>
      )}

      <AdminFormRow title="Primary Phone" inputWrapperClass="max-w-[20rem]" isRequired={getIsRequired('primaryPhone')}>
        <Controller
          name="primaryPhone"
          control={control}
          render={({ field: { value, ref, ...field }, fieldState: { invalid, error, isTouched } }) => (
            <PhoneInput
              countries={countryCodes}
              disabled={isReadOnly}
              selectAriaLabel="Phone code input"
              inputAriaLabel="Phone number input"
              value={value}
              isInvalid={invalid && isTouched}
              error={error?.message}
              {...field}
            />
          )}
        />
      </AdminFormRow>

      <AdminFormRow
        title="Secondary Phone"
        inputWrapperClass="max-w-[20rem]"
        isRequired={getIsRequired('secondaryPhone')}
      >
        <Controller
          name="secondaryPhone"
          control={control}
          render={({ field: { value, ref, ...field }, fieldState: { invalid, error, isTouched } }) => (
            <PhoneInput
              countries={countryCodes}
              disabled={isReadOnly}
              selectAriaLabel="Phone code input"
              inputAriaLabel="Phone number input"
              value={value || ''}
              isInvalid={invalid && isTouched}
              error={error?.message}
              {...field}
            />
          )}
        />
      </AdminFormRow>
    </>
  );
};
